import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { FileText, Plus, Trash2, Save, AlertCircle, Upload, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to construct proper file URLs
const getFileUrl = (fileUrl) => {
  if (!fileUrl) return '#';
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }
  const path = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
  return `${API_URL}${path}`;
};

export default function CitizenCharterManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/pages/citizen-charter`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        // Initialize with empty data
        setData({
          id: 'charter-1',
          header_title: 'Citizen Charter',
          intro_text: '',
          sections: [],
          citizen_charter_pdf: { url: null, filename: null, uploaded_at: null },
          pdf_card_text: 'Download our complete Citizen Charter',
          show_pdf_card: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await axios.post(`${API_URL}/api/pages/citizen-charter`, data, {
        headers: getAuthHeaders()
      });
      alert('Citizen Charter page updated successfully!');
    } catch (error) {
      console.error('Error saving:', error);
      setError(error.response?.data?.detail || 'Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  const handlePDFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are allowed');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/pages/citizen-charter/upload-pdf`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setData({
        ...data,
        citizen_charter_pdf: response.data.pdf_data
      });
      alert('PDF uploaded successfully!');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      setError(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const addSection = () => {
    setData({
      ...data,
      sections: [
        ...data.sections,
        { section_title: '', section_body: '' }
      ]
    });
  };

  const updateSection = (index, field, value) => {
    const updated = [...data.sections];
    updated[index][field] = value;
    setData({ ...data, sections: updated });
  };

  const deleteSection = (index) => {
    setData({
      ...data,
      sections: data.sections.filter((_, i) => i !== index)
    });
  };

  const moveSectionUp = (index) => {
    if (index === 0) return;
    const updated = [...data.sections];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setData({ ...data, sections: updated });
  };

  const moveSectionDown = (index) => {
    if (index === data.sections.length - 1) return;
    const updated = [...data.sections];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setData({ ...data, sections: updated });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-red-600">Failed to load data</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-8 h-8 text-[#0E6D2B]" />
            Citizen Charter Management
          </h1>
          <p className="text-gray-600 mt-1">Manage citizen charter content and PDF</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Header Fields */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">Page Header</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              value={data.header_title}
              onChange={(e) => setData({ ...data, header_title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Introduction Text (HTML)</label>
            <Textarea
              value={data.intro_text}
              onChange={(e) => setData({ ...data, intro_text: e.target.value })}
              rows={4}
              placeholder="<p>Enter HTML content...</p>"
            />
            <p className="text-xs text-gray-500 mt-1">You can use HTML tags for formatting</p>
          </div>
        </div>
      </div>

      {/* PDF Upload Section */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">PDF Document</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="show_pdf_card"
              checked={data.show_pdf_card}
              onChange={(e) => setData({ ...data, show_pdf_card: e.target.checked })}
              className="w-4 h-4"
            />
            <label htmlFor="show_pdf_card" className="text-sm font-medium">
              Show PDF download card on page
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">PDF Card Text</label>
            <input
              type="text"
              value={data.pdf_card_text}
              onChange={(e) => setData({ ...data, pdf_card_text: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Download our complete Citizen Charter"
            />
          </div>

          {/* Current PDF */}
          {data.citizen_charter_pdf?.url && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium mb-2">Current PDF:</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700">{data.citizen_charter_pdf.filename}</p>
                  <p className="text-xs text-gray-500">Uploaded: {data.citizen_charter_pdf.uploaded_at}</p>
                </div>
                <a
                  href={getFileUrl(data.citizen_charter_pdf.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#0E6D2B] hover:text-[#0a5a23] flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  View
                </a>
              </div>
            </div>
          )}

          {/* Upload New PDF */}
          <div>
            <label className="block text-sm font-medium mb-2">Upload PDF</label>
            <div className="flex items-center gap-4 mb-3">
              <input
                type="file"
                accept=".pdf"
                onChange={handlePDFUpload}
                className="hidden"
                id="pdf-upload"
                disabled={uploading}
              />
              <label
                htmlFor="pdf-upload"
                className={`px-4 py-2 bg-[#0E6D2B] text-white rounded-lg hover:bg-[#0a5a23] cursor-pointer flex items-center gap-2 ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Choose PDF File'}
              </label>
              <span className="text-sm text-gray-600">OR</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">PDF files only, max 10MB</p>
          </div>

          {/* PDF URL Option */}
          <div>
            <label className="block text-sm font-medium mb-2">Or PDF URL</label>
            <input
              type="text"
              value={data.citizen_charter_pdf?.url || ''}
              onChange={(e) => setData({
                ...data,
                citizen_charter_pdf: {
                  ...data.citizen_charter_pdf,
                  url: e.target.value,
                  filename: data.citizen_charter_pdf?.filename || 'Citizen_Charter.pdf'
                }
              })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="https://example.com/citizen-charter.pdf"
            />
            <p className="text-xs text-gray-500 mt-1">💡 Upload for better performance, or paste external PDF URL</p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Content Sections</h2>
          <Button onClick={addSection} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Add Section
          </Button>
        </div>

        <div className="space-y-6">
          {data.sections.map((section, idx) => (
            <div key={idx} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-700">Section {idx + 1}</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => moveSectionUp(idx)}
                    disabled={idx === 0}
                    className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveSectionDown(idx)}
                    disabled={idx === data.sections.length - 1}
                    className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => deleteSection(idx)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Section Title</label>
                  <input
                    type="text"
                    value={section.section_title}
                    onChange={(e) => updateSection(idx, 'section_title', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg bg-white"
                    placeholder="e.g., Vision, Mission, Services"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Section Body (HTML)</label>
                  <Textarea
                    value={section.section_body}
                    onChange={(e) => updateSection(idx, 'section_body', e.target.value)}
                    rows={6}
                    placeholder="<p>Enter HTML content...</p>"
                  />
                  <p className="text-xs text-gray-500 mt-1">You can use HTML tags for formatting</p>
                </div>
              </div>
            </div>
          ))}

          {data.sections.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No sections added yet. Click "Add Section" to create your first section.
            </div>
          )}
        </div>
      </div>

      {/* Save Button at Bottom */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0E6D2B] hover:bg-[#0a5a23]"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
    </AdminLayout>
  );
}
